// Los tres canales de sonido de la Academia, y a cuál pertenece cada cosa que suena.
//
// EL PROBLEMA QUE ESTO RESUELVE, dicho como estaba escrito en el marcador: **quien se satura
// con la fanfarria se queda ahora también sin lector de voz**. Había un solo interruptor
// -`alpha_muted`- y apagaba todo lo que hacía ruido. Un alumno con hipersensibilidad auditiva
// tiene una sola opción: silenciarlo entero. Y entonces pierde también las ayudas que existen
// justamente para él.
//
// Son tres porque hacen tres trabajos distintos, y ninguno sustituye a otro:
//
//   · INFORMATIVO — dice algo que hace falta saber y que no está en otro sitio. Hoy es el
//     lector de voz de las actividades. **Es una ayuda de accesibilidad, no un adorno**, y por
//     eso tiene su propio interruptor y no se apaga con los demás.
//   · INTERFAZ — confirma que lo que has hecho ha llegado: el clic, el ping al elegir, el aviso
//     de que algo está bloqueado. Sin él la plataforma se siente muerta; con él y sin lo demás
//     se sigue pudiendo trabajar.
//   · JUEGO — la fanfarria. Cofres, jefes finales, victorias, la voz de Alpha. Es lo que más
//     ruido hace y lo primero que estorba a quien se satura.
//
// LO QUE ESTE FICHERO NO HACE: sonar. Aquí sólo están las reglas -qué canal es cada cosa, qué
// se guarda, y qué pasa con quien ya había silenciado antes de que esto existiera-, sin
// WebAudio y sin React, para que se pruebe con `node` en el CI barato.

export const CANAL = {
  INFO: "informativo",
  INTERFAZ: "interfaz",
  JUEGO: "juego",
};

export const CANALES = [CANAL.INFO, CANAL.INTERFAZ, CANAL.JUEGO];

/**
 * Los tres valores del ajuste «Sonido» de la pantalla de Ajustes.
 *
 * Los define `lib/preferencias.mjs`, que es donde vive el catálogo entero de preferencias;
 * aquí se nombran para no escribirlos como cadenas sueltas en cinco sitios. Que sean los
 * mismos lo comprueba `prueba_canales.mjs`, porque una copia de tres cadenas es una copia
 * igual de peligrosa que una copia de trescientas líneas: el día que una cambie, la otra no.
 */
export const TODO = "todo";
export const SIN_JUEGO = "sin-juego";
export const SILENCIO = "silencio";

/** La llave del interruptor de antes. Se lee una vez para heredar la elección, y nada más. */
export const LLAVE_VIEJA = "alpha_muted";

/**
 * A qué canal pertenece cada sonido de `sound.js`.
 *
 * ESTA TABLA LA VIGILA UNA PRUEBA QUE LEE `sound.js`: si mañana alguien añade un sonido y no
 * lo clasifica, la prueba se pone roja con su nombre. Es la lección del auditorio —una lista
 * escrita a mano se queda corta y nadie se entera—, y aquí la lista se puede escribir porque
 * lo que se compara con ella se lee del código.
 */
export const DE_CADA_SONIDO = {
  // Interfaz: responden a algo que acabas de hacer.
  tick: CANAL.INTERFAZ,
  ping: CANAL.INTERFAZ,
  deny: CANAL.INTERFAZ,
  chime: CANAL.INTERFAZ,
  whoosh: CANAL.INTERFAZ,
  teleport: CANAL.INTERFAZ,
  // LA CEREMONIA DE ABRIR UN SITIO VA AQUÍ Y NO EN EL DEL JUEGO, y es la única entrada de esta
  // tabla que mereció pensarlo. No es ruido de partida: pasa **una vez por sitio** —diez veces
  // en la vida de una cuenta— y responde a algo que el alumno acaba de hacer fuera del mundo,
  // terminar la lección que le faltaba. Quien pide «Sin distracciones» está callando el ruido
  // de los juegos, no la noticia de que se ha ganado un sitio, así que ahí sigue sonando.
  //
  // Y NO VA AL INFORMATIVO, que fue la primera idea. Ese canal no se apaga con ninguna opción
  // —es el lector de voz, que sólo habla cuando lo pulsas— y meter ahí un sonido que suena
  // solo rompería la promesa de «Silencio». Quien pide silencio pide silencio.
  seAbre: CANAL.INTERFAZ,
  // LAS DOS DEL REPASO VAN A INTERFAZ Y NO AL JUEGO, y es la única decisión de este cambio.
  // La regla de la tabla dice que al canal del juego va lo que también está escrito en la
  // pantalla, y el acierto del repaso lo está. Pero el repaso **no es un minijuego**: es
  // estudio, y estas dos son la respuesta a lo que el alumno acaba de contestar. Quien apaga
  // «Sin distracciones» está callando el ruido de los juegos, no la señal de si va bien con lo
  // que estudió hace tres días. Si algún día molesta, se mueven a JUEGO y se escribe por qué.
  repasada: CANAL.INTERFAZ,
  oooh: CANAL.INTERFAZ,
  guardada: CANAL.INTERFAZ,
  // Juego: celebran, amenazan o hacen gracia. Nada de esto lleva información que no esté
  // también escrita en la pantalla.
  // LOS CUATRO DE UN MINIJUEGO. Van al canal del JUEGO y no al informativo, y eso es una
  // decisión: lo que dicen está también escrito en la pantalla —la tarjeta se enciende, el
  // árbol suma una nube—, así que quien apaga los sonidos del juego no se pierde nada que
  // necesite. Si algún día un minijuego depende del oído, ese sonido cambia de canal.
  acierto: CANAL.JUEGO,
  fallo: CANAL.JUEGO,
  ganas: CANAL.JUEGO,
  pierdes: CANAL.JUEGO,
  // EL ENCUENTRO CON ASTERION, las dos voces. Van al canal del juego como los cuatro de
  // arriba y por la misma razon: lo que cuentan —que el bicho esta ahi y que hay que salir—
  // lo dice tambien la pantalla, que se da la vuelta sola. Quien juega sin sonido se lleva el
  // susto en imagen.
  rugido: CANAL.JUEGO,
  susto: CANAL.JUEGO,
  arpeggio: CANAL.JUEGO,
  balbucea: CANAL.JUEGO,
  cofreEmpieza: CANAL.JUEGO,
  cofreListo: CANAL.JUEGO,
  cofreRedoble: CANAL.JUEGO,
  fanfare: CANAL.JUEGO,
  hit8bit: CANAL.JUEGO,
  hurt8bit: CANAL.JUEGO,
  punch: CANAL.JUEGO,
  boing: CANAL.JUEGO,
  tobogan: CANAL.JUEGO,
  thwack: CANAL.JUEGO,
  umbral: CANAL.JUEGO,
  // Su «mmm» y su «uuh» al contestarle en el tranvía. Va con el juego, no con la interfaz: es
  // alguien reaccionando, no un botón confirmando.
  socratesDice: CANAL.JUEGO,
  naveCabina: CANAL.JUEGO,
  naveDespega: CANAL.JUEGO,
  victory8bit: CANAL.JUEGO,
  // LOS TRES DEL TALLER. Van al canal del juego como los cuatro de arriba, y por lo mismo: lo
  // que dicen está también en la pantalla y en el propio yunque —la pieza crece o se rompe—,
  // así que quien apaga los sonidos del juego no se queda sin saber cómo va. Son el ruido del
  // sitio, no información.
  martillo: CANAL.JUEGO,
  chisporroteo: CANAL.JUEGO,
  temple: CANAL.JUEGO,
};

/**
 * LOS TRES CANALES A PARTIR DEL AJUSTE, que es la única fuente de verdad.
 *
 * Y AQUI HUBO QUE DESHACER UN DISEÑO. La primera versión de este fichero guardaba tres llaves
 * propias -`alpha_audio_informativo` y compañía- y las manejaba por su cuenta. Estaba bien
 * hasta que se miró la pantalla de Ajustes: **ya había allí un ajuste «Sonido» con estas tres
 * opciones exactas**, escrito, pintado y sin conectar a nada —sólo ponía un `data-sonido` en
 * el `<html>` que no lee nadie, así que los atajos «Sin distracciones» y «Silencio» prometían
 * callar la plataforma y no la callaban—.
 *
 * O sea que el arreglo iba a dejar DOS mandos para lo mismo, cada uno con su almacén: el botón
 * del Cielo por un lado y el ajuste por otro. Es exactamente la avería que esta rebanada venía
 * a quitar, cometida otra vez y con mejor letra. Así que las llaves propias se fueron: manda el
 * ajuste, y aquí sólo se traduce.
 *
 * LAS TRES OPCIONES NO SON TRES CASILLAS, y también es a propósito. Con casillas sueltas caben
 * estados que no quiere nadie -la fanfarria encendida y los clics apagados- y hay que
 * explicarlos. Con tres valores en fila se lee de un vistazo cuánto silencio estás pidiendo.
 *
 * Y EL INFORMATIVO NO SE APAGA CON NINGUNA, que es la razón de ser de todo esto. El lector de
 * voz sólo habla cuando lo pulsas: no hay nada de lo que protegerse, y llevárselo puesto
 * dentro de «Silencio» sería quitarle una ayuda a quien sólo pedía que se callara la música.
 */
export function canalesDe(ajuste) {
  const juego = ajuste !== SIN_JUEGO && ajuste !== SILENCIO;
  return {
    [CANAL.INFO]: true,
    [CANAL.INTERFAZ]: ajuste !== SILENCIO,
    [CANAL.JUEGO]: juego,
  };
}

/**
 * Lo que hereda quien ya había pulsado el botón de silencio antes de que esto existiera.
 *
 * Devuelve el ajuste que le corresponde, o `null` si no hay nada que heredar. Se aplica una
 * sola vez y **sólo si el alumno no ha elegido ya**: lo que él tocó manda sobre lo que se
 * deduce de una llave vieja.
 */
export function loQueHereda(ajusteGuardado, viejoMudo) {
  if (ajusteGuardado === SIN_JUEGO || ajusteGuardado === SILENCIO) return null;
  if (ajusteGuardado === TODO && viejoMudo) return SILENCIO;
  return viejoMudo ? SILENCIO : null;
}

/**
 * El canal de un sonido, y **el defecto es JUEGO a propósito**.
 *
 * Si mañana alguien añade un sonido y se olvida de clasificarlo, lo que tiene que pasar es
 * que se apague cuando el alumno apaga la fanfarria, no que se cuele en el canal que no se
 * puede apagar. Un olvido tiene que caer del lado que no hace daño.
 */
export function canalDe(sonido) {
  return DE_CADA_SONIDO[sonido] || CANAL.JUEGO;
}

/** Si un sonido concreto puede sonar con este estado. */
export function suena(sonido, estado) {
  return estado?.[canalDe(sonido)] !== false;
}

/** El ajuste que deja la casa callada del todo, y el que la deja sonar entera. */
export function conElBoton(callar) {
  return callar ? SILENCIO : TODO;
}

/** Y «está en silencio» quiere decir que no suena ninguno de los dos que son ruido. */
export function estaEnSilencio(estado) {
  return estado?.[CANAL.INTERFAZ] === false && estado?.[CANAL.JUEGO] === false;
}
